import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { CollectionPropertyType, ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionOneImageFiveTextsApi from "hooks/api/private/blocks/useSectionOneImageFiveTextsApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import {
    SectionOneImageFiveTextsBody,
    SectionOneImageFiveTextsDetails,
} from "types/api/blocks/sectionOneImageFiveTexts";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { BlockEnum, DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type SectionOneImageFiveTextsForm = {
    id: string | undefined;
    img_id: string | undefined;
    img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title: string;
            subtitle: string;
            text: string;
            button_label: string;
            button_url: string;
        };
    };
};

type SectionOneImageFiveTextsType = {
    setImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionOneImageFiveTextsForm>;
};

function useSectionOneImageFiveTexts(): SectionOneImageFiveTextsType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const {
        saveSectionOneImageFiveTexts,
        getOneSectionOneImageFiveTexts,
        deleteSectionOneImageFiveTexts,
    } = useSectionOneImageFiveTextsApi();

    const textWithoutValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    text: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [],
                    },
                    button_url: {
                        type: "simple",
                        validators: [],
                    },
                },
            },
        }),
        [],
    );

    const textWithValidators: CollectionPropertyType = React.useMemo(
        () => ({
            type: "collection",
            of: {
                type: "object",
                properties: {
                    title: {
                        type: "simple",
                        validators: [],
                    },
                    subtitle: {
                        type: "simple",
                        validators: [],
                    },
                    text: {
                        type: "simple",
                        validators: [],
                    },
                    button_label: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                    button_url: {
                        type: "simple",
                        validators: [notEmptyValidator],
                    },
                },
            },
        }),
        [],
    );

    const [schema, setSchema] = React.useState<ObjectPropertyType>(() => ({
        type: "object",
        properties: {
            id: {
                type: "simple",
                validators: [],
            },
            img_id: {
                type: "simple",
                validators: [],
            },
            img: notEmptyMedia,
            text: textWithoutValidators,
        },
    }));

    const initialValues: SectionOneImageFiveTextsForm = React.useMemo(() => {
        return {
            id: undefined,
            img_id: undefined,
            img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
                en: {
                    title: "",
                    subtitle: "",
                    text: "",
                    button_label: "",
                    button_url: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionOneImageFiveTextsForm>(
        schema,
        initialValues,
        getExtraErrorsDescription,
    );

    const { showAllError, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        setSchema((prevSchema) => {
            const newSchema = _.cloneDeep(prevSchema);

            if (
                (form.text.en.button_label ||
                    form.text.en.button_url ||
                    form.text.it.button_label ||
                    form.text.it.button_url) &&
                blockId !== BlockEnum.SectionOneImageThreeTexts
            ) {
                newSchema.properties.text = textWithValidators;
            } else {
                newSchema.properties.text = textWithoutValidators;
            }

            return newSchema;
        });
    }, [
        form.text.en.button_label,
        form.text.en.button_url,
        form.text.it.button_label,
        form.text.it.button_url,
        textWithoutValidators,
        textWithValidators,
        blockId,
    ]);

    const { setDesktopMobileImage: setImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "img",
    });

    const [sectionImageFiveTextsDetails, setSectionImageFiveTextsDetails] = React.useState<
        SectionOneImageFiveTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionOneImageFiveTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionImageFiveTextsDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionImageFiveTextsDetails, getOneSectionOneImageFiveTexts, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionImageFiveTextsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionImageFiveTextsDetails.id;
                newForm.img_id = sectionImageFiveTextsDetails.img_id;
                newForm.text = sectionImageFiveTextsDetails.text;
                newForm.img = {
                    id: sectionImageFiveTextsDetails.img.id,
                    desktop: createAttachmentType({
                        id: sectionImageFiveTextsDetails.img.desktop.id,
                        label: "Desktop",
                        filename: sectionImageFiveTextsDetails.img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: sectionImageFiveTextsDetails.img.mobile.id,
                        label: "Mobile",
                        filename: sectionImageFiveTextsDetails.img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, sectionImageFiveTextsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionImage5TextsBody: SectionOneImageFiveTextsBody = {
                    id: blockGroupCompositionId,
                    img: {
                        id: form.img.id,
                        desktop: converterAttachmentToFileWithPayload(form.img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            subtitle: form.text.it.subtitle,
                            text: form.text.it.text,
                            button_label: form.text.it.button_label,
                            button_url: form.text.it.button_url,
                        },
                        en: {
                            title: form.text.en.title,
                            subtitle: form.text.en.subtitle,
                            text: form.text.en.text,
                            button_label: form.text.en.button_label,
                            button_url: form.text.en.button_url,
                        },
                    },
                };

                const [apiCall] = saveSectionOneImageFiveTexts(sectionImage5TextsBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionOneImageFiveTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionOneImageFiveTextsType => ({
            buttonDelete: deleteSectionOneImageFiveTexts,
            setImage,
            formHook,
        }),
        [deleteSectionOneImageFiveTexts, setImage, formHook],
    );
}

export default useSectionOneImageFiveTexts;
